/**
 * BadgeCounter Component
 * 
 * Badge numerico per visualizzare contatori di notifiche
 * con colori dinamici basati su priorità.
 * 
 * Usage:
 *   <BadgeCounter count={5} priority="urgente" />
 *   <BadgeCounter count={2} priority="importante" size="sm" />
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';

const BadgeCounter = ({ 
    count = 0, 
    priority = 'info', 
    size = 'normal',
    className = '',
    showZero = false,
    maxCount = 99
}) => {
    // Non mostrare badge se count è 0 e showZero è false
    if (count === 0 && !showZero) {
        return null;
    }

    // Mappa priorità a colori Bootstrap
    const priorityColors = {
        urgente: 'danger',      // Rosso - azione immediata
        importante: 'warning',   // Giallo - richiede attenzione
        info: 'primary'          // Blu - informazioni generali
    };

    const bgColor = priorityColors[priority] || 'primary';
    const displayCount = count > maxCount ? `${maxCount}+` : count;

    // Stili dinamici basati su size (precedentemente in styled-jsx)
    const sizeStyles = {
        xs: { padding: '0.15rem 0.35rem', fontSize: '0.65rem', fontWeight: 600, minWidth: '18px' },
        sm: { padding: '0.2rem 0.45rem', fontSize: '0.7rem', fontWeight: 600, minWidth: '20px' },
        normal: { minWidth: '24px', fontSize: '0.75rem' },
        lg: { padding: '0.35rem 0.6rem', fontSize: '0.85rem', fontWeight: 700, minWidth: '26px' }
    };

    return (
        <>
            <Badge 
                bg={bgColor} 
                pill 
                className={`ms-2 ${className}`}
                style={sizeStyles[size] || sizeStyles.normal}
            >
                {displayCount}
            </Badge>

            {/* CSS Custom per sizing - styled-jsx non supportato in React normale */}
            {/* Gli stili sono già applicati tramite style={{...}} inline sopra */}
        </>
    );
};

BadgeCounter.propTypes = {
    count: PropTypes.number,
    priority: PropTypes.oneOf(['info', 'importante', 'urgente']),
    size: PropTypes.oneOf(['xs', 'sm', 'normal', 'lg']),
    className: PropTypes.string,
    showZero: PropTypes.bool,
    maxCount: PropTypes.number
};

export default BadgeCounter;
